/**
 * Expeditions together (decision 0043) in the browser: the party's run kept on the device, the
 * best of runs played together, and the line to the party's room (spec/formats.md, Parties). The
 * controller plays the run; the interface shows the members (`ui/party.ts`).
 */
import {
  type Action,
  type ActionKind,
  distanceSq,
  Game,
  metresFromDistanceSq,
  parseRunRecord,
  type RunRecord,
} from "@mistsweeper/core";
import type { EndlessBest } from "./runs.ts";
import { getJson, remove, setJson } from "./storage.ts";

/** The party's run on this device, `mistsweeper.v1.run.together`, never synced. */
export interface TogetherRun {
  readonly v: 1;
  readonly code: string;
  readonly token: string;
  readonly you: number;
  readonly record: RunRecord;
}

/** Eight letters since 2026-11-01; the six of the first parties still open. */
export const CODE = /^[a-hjkmnp-z2-9]{6}(?:[a-hjkmnp-z2-9]{2})?$/;

/** How many the trial lets into a party: the Worker's PARTY_LIMIT, which the link's info also says. */
export const PARTY_LIMIT = 6;

export function loadTogether(): TogetherRun | null {
  const saved = getJson<TogetherRun>("run.together");
  if (saved?.v !== 1 || typeof saved.code !== "string" || !CODE.test(saved.code)) {
    return null;
  }
  if (typeof saved.token !== "string" || typeof saved.you !== "number") return null;
  try {
    return { ...saved, record: parseRunRecord(saved.record) };
  } catch {
    return null;
  }
}

export function saveTogether(run: TogetherRun): void {
  setJson("run.together", run);
}

export function clearTogether(): void {
  remove("run.together");
}

/** The best of runs played together, a best of its own (decision 0043), as EndlessBest. */
export function loadTogetherBest(): EndlessBest | null {
  const best = getJson<EndlessBest>("together.best");
  return best && best.v === 1 && typeof best.metres === "number" ? best : null;
}

export function saveTogetherBest(best: EndlessBest): void {
  setJson("together.best", best);
}

/** What one member found: the tiles their actions revealed and the furthest of them. */
export interface MemberTally {
  readonly tiles: number;
  readonly metres: number;
}

/**
 * Each member's tiles, by replaying the record and giving every tile an action revealed to the
 * member who took it; the cabin's opening reveal is nobody's. Derived for the interface, never
 * kept, so it is no rule of the core (decision 0043).
 */
export function memberTiles(record: RunRecord): Map<number, MemberTally> {
  const game = Game.create({
    seed: record.seed,
    mode: record.mode,
    day: record.day,
    startedAt: record.startedAt,
    ...(record.party !== undefined ? { party: record.party } : {}),
  });
  const out = new Map<number, { tiles: number; d2: number }>();
  for (const action of record.actions) {
    const member = action[3] ?? 0;
    const result = game.apply(action);
    if (result.revealed.length === 0) continue;
    const t = out.get(member) ?? { tiles: 0, d2: 0 };
    t.tiles += result.revealed.length;
    for (const c of result.revealed) t.d2 = Math.max(t.d2, distanceSq(c.x, c.y));
    out.set(member, t);
  }
  return new Map(
    [...out].map(([m, t]) => [m, { tiles: t.tiles, metres: metresFromDistanceSq(t.d2) }]),
  );
}

/** The code in a link to a party, `/x/<code>`, or null. */
export function codeInPath(path: string): string | null {
  const m = /^\/x\/([a-z0-9]+)\/?$/.exec(path);
  return m && CODE.test(m[1] as string) ? (m[1] as string) : null;
}

export function partyLink(code: string): string {
  return `${location.origin}/x/${code}`;
}

export interface MemberView {
  readonly m: number;
  readonly name: string;
  readonly banner: string;
  readonly online: boolean;
  /** The host took them out: kept in the record, left out of the row. */
  readonly removed?: boolean;
}

export type Refusal = "full" | "over" | "gone" | "name" | "request" | "removed" | "closed" | "busy";

export interface PartyInfo {
  readonly members: number;
  readonly limit: number;
  readonly over: boolean;
  readonly locked?: boolean;
  readonly metres: number;
  readonly host: string;
}

/** What a party's link says before one joins, or null where there is no such party. */
export async function partyInfo(code: string): Promise<PartyInfo | null> {
  try {
    const response = await fetch(`/api/v1/party/${code}`);
    if (!response.ok) return null;
    return (await response.json()) as PartyInfo;
  } catch {
    return null;
  }
}

/** Starts a party from the host's run; the answer, or the reason it could not. */
export async function startParty(
  record: RunRecord,
  name: string,
  banner: string,
): Promise<{ code: string; token: string; you: number } | { error: string }> {
  try {
    const response = await fetch("/api/v1/party", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ record, name, banner }),
    });
    const body = (await response.json()) as { code?: string; token?: string; error?: string };
    if (response.ok && body.code && body.token)
      return { code: body.code, token: body.token, you: 0 };
    return { error: body.error ?? "unavailable" };
  } catch {
    return { error: "offline" };
  }
}

export interface ConnectionEvents {
  welcome(
    you: number,
    token: string,
    record: RunRecord,
    members: readonly MemberView[],
    locked: boolean,
  ): void;
  act(action: Action): void;
  members(members: readonly MemberView[], locked: boolean): void;
  look(member: number, camera: readonly number[]): void;
  refused(why: Refusal): void;
  /** The line went and is being tried again; `true` again once it is back. */
  line(up: boolean): void;
}

/**
 * The line to a party's room: says hello with the token once it has one, takes its place again
 * after a lost line, and passes every message on. Closed for good by `close`.
 */
export class PartyConnection {
  readonly code: string;
  private token: string | undefined;
  private readonly name: string;
  private readonly banner: string;
  /** Daniel's key when this line only watches (decision 0043). */
  private readonly watch: string | undefined;
  private readonly events: ConnectionEvents;
  private socket: WebSocket | null = null;
  private closed = false;
  private tries = 0;
  private up = false;
  /** The line went at least once since the last welcome: the next welcome says it is back. */
  private dropped = false;

  constructor(
    code: string,
    hello: { token?: string; name: string; banner: string; watch?: string },
    events: ConnectionEvents,
  ) {
    this.code = code;
    this.token = hello.token;
    this.name = hello.name;
    this.banner = hello.banner;
    this.watch = hello.watch;
    this.events = events;
    this.open();
  }

  get online(): boolean {
    return this.up;
  }

  private open(): void {
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/v1/party/${this.code}`;
    const socket = new WebSocket(url);
    this.socket = socket;
    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          t: "hello",
          ...(this.token ? { token: this.token } : {}),
          name: this.name,
          banner: this.banner,
          ...(this.watch ? { watch: this.watch } : {}),
        }),
      );
    };
    socket.onmessage = (event) => this.receive(String(event.data));
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.up) {
        this.dropped = true;
        this.events.line(false);
      }
      this.up = false;
      // Refused, replaced by another tab of the same member, or closed by the player: no retry.
      if (this.closed || event.code === 4001 || event.code === 4000) return;
      this.tries += 1;
      const wait = Math.min(15_000, 500 * 2 ** Math.min(this.tries, 5));
      setTimeout(() => {
        if (!this.closed) this.open();
      }, wait);
    };
  }

  private receive(text: string): void {
    let msg: { t?: string } & Record<string, unknown>;
    try {
      msg = JSON.parse(text) as typeof msg;
    } catch {
      return;
    }
    if (msg.t === "welcome") {
      let record: RunRecord;
      try {
        record = parseRunRecord(msg.record);
      } catch {
        return;
      }
      this.token = msg.token as string;
      this.tries = 0;
      this.up = true;
      this.events.welcome(
        msg.you as number,
        this.token,
        record,
        msg.members as MemberView[],
        msg.locked === true,
      );
      if (this.dropped) this.events.line(true);
      this.dropped = false;
    } else if (msg.t === "act" && Array.isArray(msg.a)) {
      this.events.act(msg.a as unknown as Action);
    } else if (msg.t === "members" && Array.isArray(msg.members)) {
      this.events.members(msg.members as MemberView[], msg.locked === true);
    } else if (msg.t === "look" && Array.isArray(msg.c)) {
      this.events.look(msg.m as number, msg.c as number[]);
    } else if (msg.t === "refused") {
      this.closed = true;
      this.events.refused(msg.why as Refusal);
    }
  }

  act(kind: ActionKind, x: number, y: number): void {
    this.send({ t: "act", a: [kind, x, y] });
  }

  /** The host's: take a member out of the party. */
  remove(member: number): void {
    this.send({ t: "remove", m: member });
  }

  /** The host's: close the party to new members, or open it again. */
  lock(on: boolean): void {
    this.send({ t: "lock", on });
  }

  look(camera: readonly number[]): void {
    this.send({ t: "look", c: camera });
  }

  private send(msg: object): void {
    if (this.up && this.socket?.readyState === WebSocket.OPEN)
      this.socket.send(JSON.stringify(msg));
  }

  close(): void {
    this.closed = true;
    this.socket?.close(1000, "left");
    this.socket = null;
    this.up = false;
  }
}
